import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MessageCircle, ShieldCheck } from "lucide-react";
import { OWNER, PHONE_DIGITS } from "@/lib/constants";
import { useLang } from "@/lib/i18n";

const EASE = [0.16, 1, 0.3, 1];

export const Hero = () => {
  const { t } = useLang();
  const artRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: artRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);
  const whatsappUrl = `https://wa.me/${PHONE_DIGITS}?text=${encodeURIComponent("Hello " + OWNER + ", I would like to enquire about a room near IGI Airport.")}`;

  return (
    <section className="hero" id="home">
      <div className="hero-copy">
        <motion.div className="eyebrow" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }} data-testid="hero-eyebrow">
          <span className="eyebrow-dot" /> {t.hero.eyebrow}
        </motion.div>
        <h1 className="hero-title" data-testid="hero-title">
          {t.hero.lines.map((line, i) => (
            <span className="mask-line" key={line}>
              <motion.span
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.15 + i * 0.14, duration: 0.9, ease: EASE }}
              >
                {i === 1 ? <em>{line}</em> : line}
              </motion.span>
            </span>
          ))}
        </h1>
        <motion.p
          className="hero-lede"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.65, duration: 0.7, ease: EASE }}
        >
          {t.hero.lede}
        </motion.p>
        <motion.div
          className="hero-actions"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.7, ease: EASE }}
        >
          <a className="button button-gold" href="#enquiry" data-testid="hero-request-room-button">
            {t.hero.ctaPrimary} <ArrowRight size={17} />
          </a>
          <a className="button button-ghost" href={whatsappUrl} target="_blank" rel="noreferrer" data-testid="hero-whatsapp-button">
            <MessageCircle size={17} /> {t.hero.ctaWhatsapp}
          </a>
        </motion.div>
        <motion.div className="trust-line" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1, duration: 0.8 }}>
          <ShieldCheck size={16} /> <span>{t.hero.trust}</span>
        </motion.div>
      </div>
      <motion.div
        className="hero-art"
        ref={artRef}
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.35, duration: 1, ease: EASE }}
        data-testid="hero-photo"
      >
        <motion.img src="/images/hero.webp" alt="IGI Airport terminal at dusk — illustrative" style={{ y }} />
        <div className="art-label">
          <span className="art-label-dot" />
          <span>
            <strong>{t.hero.labelTitle}</strong>
            <small>{t.hero.labelSub}</small>
          </span>
        </div>
      </motion.div>
    </section>
  );
};
